import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Injector, ElementRef, Renderer2, HostListener, HostBinding, ViewChild, Output, Input, Inject, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, Validators, NgControl, NgModel, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask, NgxMaskPipe } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { DirectivesModule, MaskSharedPropertiesBase } from '@windmill/ng-windmill/shared';
import * as i1 from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';

const WINDMILL_INPUT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WindmillInputComponent),
    multi: true
};
const windmillComponents = [CentricIcon2Component, DirectivesModule];
/**
 * To use this component in a module, you can import the `WindmillInputModule`:
 * @example
 * ```typescript
 * import { WindmillInputModule } from '@windmill/ng-windmill/input';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillInputComponent`:
 * @example
 * ```typescript
 * import { WindmillInputComponent } from '@windmill/ng-windmill/input';
 * ```
 */
class WindmillInputComponent extends MaskSharedPropertiesBase {
    injector;
    elementRef;
    render2;
    _autofillMonitor;
    /**
     * Type of the input
     */
    type = 'text';
    /**
     * Max length of the component
     */
    maxLength;
    /**
     * Min length of the component
     */
    minLength;
    /**
     * Maximum value of the input
     */
    max;
    /**
     * Minimum value of the input
     */
    min;
    /**
     * Whether or not an input should have autocomplete enable
     */
    autocomplete = 'off';
    /**
     * Name for input
     */
    name;
    /**
     * Could be used for adding a custom function for validation for success of component.
     */
    successFunction;
    /**
     * Could be used for adding a custom function for validation for warning of component.
     */
    warningFunction;
    // Waiting documentation from ngx-mask: https://github.com/JsDaddy/ngx-mask/issues/842
    shownMaskExpression = '';
    /**
     * ***[deprecated]*** Does not exist on ngx-mask
     * @deprecated Does not exist on ngx-mask
     */
    showTemplate = false;
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the input in an editable state
     */
    backgroundColor;
    /**
     * Emits when value of the input will change
     */
    inputValueChanged = new EventEmitter();
    /**
     * Emits when value of the input will be deleted
     */
    clearValue = new EventEmitter();
    /**
     * Emits the value when input has lost focus.
     */
    innerBlur = new EventEmitter();
    value;
    inputValue;
    input;
    inputContainer = true;
    _inputElement = null;
    _windmillInputElement;
    _autofillSubscription = Subscription.EMPTY;
    get hasValue() {
        return this.value || this.value === 0;
    }
    get getPlaceholder() {
        return this.hasValue ? null : this.readonly ? '-' : this.placeholder;
    }
    get showClearButton() {
        return Boolean(this.hasClearIcon && (this.value || this.value === 0) && !this.disabled && !this.readonly);
    }
    get hasInvalidClass() {
        return this.hasInvalidState && !this.hasSuccessState && !this.hasWarningState;
    }
    get hasInvalidState() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get hasSuccessState() {
        if (typeof this.successFunction !== 'undefined') {
            return this.successFunction();
        }
        return false;
    }
    get hasWarningState() {
        if (typeof this.warningFunction !== 'undefined') {
            return this.warningFunction();
        }
        return false;
    }
    get isRequired() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    get isInvalid() {
        return this.hasInvalidState && !this.disabled && !this.readonly;
    }
    get showValidationMessages() {
        return (this.hasInvalidState || this.hasSuccessState || this.hasWarningState) && !this.disabled && !this.readonly;
    }
    constructor(injector, elementRef, render2, _autofillMonitor) {
        super(render2, elementRef);
        this.injector = injector;
        this.elementRef = elementRef;
        this.render2 = render2;
        this._autofillMonitor = _autofillMonitor;
    }
    ngAfterViewInit() {
        this._inputElement = this.elementRef.nativeElement.querySelector('input');
        this.toggleAutofillClass();
    }
    ngOnDestroy() {
        this._autofillSubscription.unsubscribe();
        this._autofillMonitor.stopMonitoring(this._inputElement);
    }
    onKeydown(event) {
        const timeout = 10;
        if (event.key !== 'Backspace' && event.key !== 'Enter' && event.key !== 'Escape') {
            return;
        }
        if (this.value !== null && event.target?.selectionEnd === 0 && event.key === 'Backspace') {
            event.preventDefault();
            setTimeout(() => {
                this.input.nativeElement.focus();
            }, timeout);
        }
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    writeValue(newValue) {
        this.value = newValue;
        const inputNewValue = newValue === null || typeof newValue === 'undefined' ? '' : String(newValue);
        this.inputValue = inputNewValue;
    }
    updateValue(newValue) {
        this.writeValue(newValue);
        if (this.injector.get(NgControl) instanceof NgModel) {
            this.onChange(newValue);
        }
    }
    onValueChanged(event) {
        let formControlValue = this.getFormControl().value;
        this.inputValue = event.target.value;
        if (this.mask && formControlValue) {
            this.updateValue(formControlValue);
        }
        else {
            const newValue = this.inputValue;
            formControlValue = this.mask ? formControlValue : this.inputValue;
            this.updateValue(newValue);
        }
        this.inputValueChanged.emit(formControlValue);
        this.getFormControl().markAsDirty();
        event.stopPropagation();
    }
    onBlur() {
        this.innerBlur.emit(this.value);
        this.onTouched();
    }
    onClearValue(event) {
        event.stopPropagation();
        this.getFormControl().markAsDirty();
        this.getFormControl().patchValue(null);
        this.input.nativeElement.focus();
        this.inputValue = '';
        this.clearValue.emit(this.value);
    }
    toggleAutofillClass() {
        this._windmillInputElement = this.elementRef.nativeElement.getElementsByClassName('windmill-input')[0];
        this._autofillSubscription = this._autofillMonitor.monitor(this._inputElement).subscribe(event => {
            if (event.isAutofilled) {
                this.render2.addClass(this._windmillInputElement, 'text-field-autofilled');
            }
            else {
                this.render2.removeClass(this._windmillInputElement, 'text-field-autofilled');
            }
        });
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        if (this._inputElement) {
            this._inputElement.scrollLeft = 0;
        }
    }
    get backgroundColorValue() {
        return this.readonly || this.disabled ? null : this.backgroundColor;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputComponent, deps: [{ token: forwardRef(() => Injector) }, { token: forwardRef(() => ElementRef) }, { token: forwardRef(() => Renderer2) }, { token: i1.AutofillMonitor }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillInputComponent, isStandalone: true, selector: "windmill-input", inputs: { type: "type", maxLength: "maxLength", minLength: "minLength", max: "max", min: "min", autocomplete: "autocomplete", name: "name", successFunction: "successFunction", warningFunction: "warningFunction", shownMaskExpression: "shownMaskExpression", showTemplate: "showTemplate", backgroundColor: "backgroundColor" }, outputs: { inputValueChanged: "inputValueChanged", clearValue: "clearValue", innerBlur: "innerBlur" }, host: { listeners: { "keydown": "onKeydown($event)" }, properties: { "class.centric-input-container": "this.inputContainer" } }, providers: [WINDMILL_INPUT_VALUE_ACCESSOR, provideNgxMask()], viewQueries: [{ propertyName: "input", first: true, predicate: ["inputElement"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"windmill-input {{ size }}\"\r\n  [class.has-value]=\"hasValue\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.input-invalid]=\"hasInvalidClass\"\r\n  [class.input-success]=\"hasSuccessState\"\r\n  [class.input-warning]=\"hasWarningState\"\r\n  [style.backgroundColor]=\"backgroundColorValue\">\r\n  <input\r\n    #inputElement\r\n    class=\"input-element\"\r\n    [formControl]=\"getFormControl()\"\r\n    [attr.placeholder]=\"getPlaceholder\"\r\n    [readonly]=\"readonly\"\r\n    [attr.minlength]=\"minLength\"\r\n    [attr.maxlength]=\"maxLength\"\r\n    [attr.min]=\"min\"\r\n    [attr.max]=\"max\"\r\n    [attr.title]=\"title\"\r\n    [attr.type]=\"type\"\r\n    [attr.id]=\"id\"\r\n    [attr.data-testid]=\"testInputId\"\r\n    [attr.aria-required]=\"isRequired\"\r\n    [attr.aria-invalid]=\"isInvalid\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [autocomplete]=\"autocomplete\"\r\n    [attr.name]=\"name\"\r\n    [mask]=\"mask\"\r\n    [validation]=\"validation\"\r\n    [specialCharacters]=\"specialCharacters\"\r\n    [patterns]=\"patterns\"\r\n    [prefix]=\"prefix\"\r\n    [suffix]=\"suffix\"\r\n    [dropSpecialCharacters]=\"dropSpecialCharacters\"\r\n    [showMaskTyped]=\"showMaskTyped\"\r\n    [allowNegativeNumbers]=\"allowNegativeNumbers\"\r\n    [placeHolderCharacter]=\"placeHolderCharacter\"\r\n    [clearIfNotMatch]=\"clearIfNotMatch\"\r\n    [thousandSeparator]=\"thousandSeparator\"\r\n    [decimalMarker]=\"decimalMarker\"\r\n    [separatorLimit]=\"separatorLimit\"\r\n    [hiddenInput]=\"hiddenInput\"\r\n    [shownMaskExpression]=\"shownMaskExpression\"\r\n    (input)=\"onValueChanged($event)\"\r\n    (blur)=\"onBlur()\" />\r\n  @if (showClearButton) {\r\n    <button\r\n      class=\"clear-button\"\r\n      type=\"button\"\r\n      [attr.aria-label]=\"ariaLabelClearIcon\"\r\n      (click)=\"onClearValue($event)\">\r\n      <centric-icon2\r\n        class=\"clear-icon\"\r\n        [svgIcon]=\"'cancel_r'\"\r\n        [size]=\"'16'\">\r\n      </centric-icon2>\r\n    </button>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    @if (hasInvalidState) {\r\n      <ng-content select=\"[errors]\"></ng-content>\r\n    }\r\n    @if (hasWarningState) {\r\n      <ng-content select=\"[warnings]\"></ng-content>\r\n    }\r\n    @if (hasSuccessState) {\r\n      <ng-content select=\"[success]\"></ng-content>\r\n    }\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: NgxMaskDirective, selector: "input[mask], textarea[mask]", inputs: ["mask", "specialCharacters", "patterns", "prefix", "suffix", "thousandSeparator", "decimalMarker", "dropSpecialCharacters", "hiddenInput", "showMaskTyped", "placeHolderCharacter", "shownMaskExpression", "clearIfNotMatch", "validation", "separatorLimit", "allowNegativeNumbers", "leadZeroDateTime", "leadZero", "triggerOnMaskChange", "apm", "inputTransformFn", "outputTransformFn", "keepCharacterPositions", "instantPrefix"], outputs: ["maskFilled"], exportAs: ["mask", "ngxMask"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "ngmodule", type: DirectivesModule }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-input', encapsulation: ViewEncapsulation.None, imports: [FormsModule, ReactiveFormsModule, NgxMaskDirective, ...windmillComponents], providers: [WINDMILL_INPUT_VALUE_ACCESSOR, provideNgxMask()], standalone: true, template: "<div\r\n  class=\"windmill-input {{ size }}\"\r\n  [class.has-value]=\"hasValue\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.input-invalid]=\"hasInvalidClass\"\r\n  [class.input-success]=\"hasSuccessState\"\r\n  [class.input-warning]=\"hasWarningState\"\r\n  [style.backgroundColor]=\"backgroundColorValue\">\r\n  <input\r\n    #inputElement\r\n    class=\"input-element\"\r\n    [formControl]=\"getFormControl()\"\r\n    [attr.placeholder]=\"getPlaceholder\"\r\n    [readonly]=\"readonly\"\r\n    [attr.minlength]=\"minLength\"\r\n    [attr.maxlength]=\"maxLength\"\r\n    [attr.min]=\"min\"\r\n    [attr.max]=\"max\"\r\n    [attr.title]=\"title\"\r\n    [attr.type]=\"type\"\r\n    [attr.id]=\"id\"\r\n    [attr.data-testid]=\"testInputId\"\r\n    [attr.aria-required]=\"isRequired\"\r\n    [attr.aria-invalid]=\"isInvalid\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [autocomplete]=\"autocomplete\"\r\n    [attr.name]=\"name\"\r\n    [mask]=\"mask\"\r\n    [validation]=\"validation\"\r\n    [specialCharacters]=\"specialCharacters\"\r\n    [patterns]=\"patterns\"\r\n    [prefix]=\"prefix\"\r\n    [suffix]=\"suffix\"\r\n    [dropSpecialCharacters]=\"dropSpecialCharacters\"\r\n    [showMaskTyped]=\"showMaskTyped\"\r\n    [allowNegativeNumbers]=\"allowNegativeNumbers\"\r\n    [placeHolderCharacter]=\"placeHolderCharacter\"\r\n    [clearIfNotMatch]=\"clearIfNotMatch\"\r\n    [thousandSeparator]=\"thousandSeparator\"\r\n    [decimalMarker]=\"decimalMarker\"\r\n    [separatorLimit]=\"separatorLimit\"\r\n    [hiddenInput]=\"hiddenInput\"\r\n    [shownMaskExpression]=\"shownMaskExpression\"\r\n    (input)=\"onValueChanged($event)\"\r\n    (blur)=\"onBlur()\" />\r\n  @if (showClearButton) {\r\n    <button\r\n      class=\"clear-button\"\r\n      type=\"button\"\r\n      [attr.aria-label]=\"ariaLabelClearIcon\"\r\n      (click)=\"onClearValue($event)\">\r\n      <centric-icon2\r\n        class=\"clear-icon\"\r\n        [svgIcon]=\"'cancel_r'\"\r\n        [size]=\"'16'\">\r\n      </centric-icon2>\r\n    </button>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    @if (hasInvalidState) {\r\n      <ng-content select=\"[errors]\"></ng-content>\r\n    }\r\n    @if (hasWarningState) {\r\n      <ng-content select=\"[warnings]\"></ng-content>\r\n    }\r\n    @if (hasSuccessState) {\r\n      <ng-content select=\"[success]\"></ng-content>\r\n    }\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i1.AutofillMonitor }], propDecorators: { type: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], minLength: [{
                type: Input
            }], max: [{
                type: Input
            }], min: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], name: [{
                type: Input
            }], successFunction: [{
                type: Input
            }], warningFunction: [{
                type: Input
            }], shownMaskExpression: [{
                type: Input
            }], showTemplate: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], inputValueChanged: [{
                type: Output
            }], clearValue: [{
                type: Output
            }], innerBlur: [{
                type: Output
            }], input: [{
                type: ViewChild,
                args: ['inputElement']
            }], inputContainer: [{
                type: HostBinding,
                args: ['class.centric-input-container']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const ngxComponents = [NgxMaskDirective, NgxMaskPipe];
const windmillModules = [CentricIcon2Module, DirectivesModule];
const moduleComponents = [WindmillInputComponent];
class WindmillInputModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputModule, imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective, NgxMaskPipe, CentricIcon2Module, DirectivesModule, WindmillInputComponent], exports: [WindmillInputComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputModule, providers: [provideNgxMask()], imports: [CommonModule, FormsModule, ReactiveFormsModule, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, ...ngxComponents, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents],
                    providers: [provideNgxMask()],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WINDMILL_INPUT_VALUE_ACCESSOR, WindmillInputComponent, WindmillInputModule };
